import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  Checkbox,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  styled,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  VpnKey,
} from "@mui/icons-material";
import axios from "axios";

import useAuth from "app/hooks/useAuth";
import useNavigatorOnLine from "app/hooks/useNavigationOnline";

// ─── Styled Components ──────────────────────────────────────────────────────

const StyledRoot = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
  padding: "16px",
}));

const GlassCard = styled(Card)(() => ({
  maxWidth: 900,
  width: "100%",
  borderRadius: 20,
  overflow: "hidden",
  boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
  background: "rgba(255, 255, 255, 0.04)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.08)",
}));

const LeftPanel = styled(Box)(() => ({
  background: "linear-gradient(160deg, #667eea, #764ba2)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 32px",
  minHeight: 480,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.07)",
    top: -80,
    right: -80,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    width: 200,
    height: 200,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
    bottom: -60,
    left: -60,
  },
}));

const RightPanel = styled(Box)(() => ({
  padding: "40px 36px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  background: "rgba(15, 12, 41, 0.6)",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.35)" },
    "&.Mui-focused fieldset": { borderColor: "#667eea" },
    "&.Mui-disabled": { opacity: 0.5 },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.55)",
    "&.Mui-focused": { color: "#a78bfa" },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
  },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
    color: "rgba(255,255,255,0.4)",
  },
  "& .MuiFormHelperText-root": { color: "#f87171" },
}));

// ─── Validation ──────────────────────────────────────────────────────────────

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(4, "Password must be at least 4 characters")
    .required("Password is required"),
  email: Yup.string().required("Email / Username is required"),
  otp: Yup.string().when("$otpRequired", {
    is: true,
    then: (s) => s.length(6, "OTP must be 6 digits").required("OTP is required"),
  }),
});

// ─── Component ───────────────────────────────────────────────────────────────

export default function Login() {
  const navigate = useNavigate();
  const { login, verifyOTP } = useAuth();
  const isOnline = useNavigatorOnLine();

  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const handleFormSubmit = async (values) => {
    setLoading(true);
    setError("");
    setInfo("");

    try {
      if (otpSent) {
        await verifyOTP(values.email, values.password, values.otp, values.remember);
        formik.setFieldValue("password", "");
        navigate("/dashboard", { replace: true });
      } else {
        await login(values.email, values.password);
        setOtpSent(true);
        setInfo("OTP sent! Please check your registered email.");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message || (otpSent ? "Invalid OTP. Please try again." : "Login failed. Please check your credentials.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = formik.values.email;
    if (!email) {
      setError("Please enter your email address first.");
      return;
    }
    try {
      const result = await axios.post(
        "https://backend.dahiyainsurance.com/resetemail.php",
        { email }
      );
      if (result.status === 200 && result.data.status === 1) {
        setInfo("Password reset email sent. Please check your inbox.");
      } else {
        setError("Failed to send reset email. Please try again.");
      }
    } catch {
      setError("An error occurred while sending the reset email.");
    }
  };

  const formik = useFormik({
    initialValues: { email: "", password: "", otp: "", remember: false },
    validationSchema,
    context: { otpRequired: otpSent },
    onSubmit: handleFormSubmit,
  });

  return (
    <StyledRoot>
      <GlassCard>
        <Grid container>
          {/* ── Left decorative panel ── */}
          <Grid item sm={5} xs={12}>
            <LeftPanel>
              <Box
                component="img"
                src="/assets/images/illustrations/dreamer.svg"
                alt="Login illustration"
                sx={{ width: "75%", maxWidth: 240, mb: 3, position: "relative", zIndex: 1 }}
              />
              <Typography
                variant="h5"
                fontWeight={700}
                color="#fff"
                textAlign="center"
                sx={{ position: "relative", zIndex: 1 }}
              >
                Dahiya Insurance
              </Typography>
              <Typography
                variant="body2"
                color="rgba(255,255,255,0.7)"
                textAlign="center"
                mt={1}
                sx={{ position: "relative", zIndex: 1 }}
              >
                LIC Agency Management Portal
              </Typography>
            </LeftPanel>
          </Grid>

          {/* ── Right form panel ── */}
          <Grid item sm={7} xs={12}>
            <RightPanel>
              <Typography variant="h5" fontWeight={700} color="#fff" mb={0.5}>
                {otpSent ? "Verify OTP" : "Welcome back"}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.5)" mb={3}>
                {otpSent
                  ? "Enter the 6-digit OTP sent to your email"
                  : "Sign in to your account to continue"}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
              {info && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  {info}
                </Alert>
              )}

              <form onSubmit={formik.handleSubmit} autoComplete="off">
                {/* Hidden dummy inputs to prevent browser credential save/autofill heuristics */}
                <input
                  type="text"
                  name="fake_username"
                  style={{ display: "none" }}
                  aria-hidden="true"
                  tabIndex={-1}
                  autoComplete="off"
                />
                <input
                  type="password"
                  name="fake_password"
                  style={{ display: "none" }}
                  aria-hidden="true"
                  tabIndex={-1}
                  autoComplete="new-password"
                />

                <StyledTextField
                  disabled={otpSent}
                  fullWidth
                  size="small"
                  name="email"
                  label="Email / Username"
                  variant="outlined"
                  autoComplete="off"
                  inputProps={{
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-1p-ignore": "true",
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  helperText={formik.touched.email && formik.errors.email}
                  error={Boolean(formik.errors.email && formik.touched.email)}
                  sx={{ mb: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  disabled={otpSent}
                  fullWidth
                  size="small"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  variant="outlined"
                  autoComplete="new-password"
                  inputProps={{
                    autoComplete: "new-password",
                    "data-lpignore": "true",
                    "data-1p-ignore": "true",
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  helperText={formik.touched.password && formik.errors.password}
                  error={Boolean(formik.errors.password && formik.touched.password)}
                  sx={{ mb: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((p) => !p)}
                          sx={{ color: "rgba(255,255,255,0.4)" }}
                          disabled={otpSent}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {otpSent && (
                  <StyledTextField
                    fullWidth
                    size="small"
                    name="otp"
                    type="text"
                    label="One-Time Password"
                    variant="outlined"
                    autoComplete="one-time-code"
                    inputProps={{
                      maxLength: 6,
                      autoComplete: "one-time-code",
                      "data-lpignore": "true",
                      "data-1p-ignore": "true",
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.otp}
                    onChange={formik.handleChange}
                    helperText={formik.touched.otp && formik.errors.otp}
                    error={Boolean(formik.errors.otp && formik.touched.otp)}
                    sx={{ mb: 2.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKey fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Checkbox
                      size="small"
                      name="remember"
                      onChange={formik.handleChange}
                      checked={formik.values.remember}
                      sx={{ padding: 0.5, color: "rgba(255,255,255,0.4)", "&.Mui-checked": { color: "#a78bfa" } }}
                    />
                    <Typography variant="body2" color="rgba(255,255,255,0.6)">
                      Remember me
                    </Typography>
                  </Box>

                  <NavLink
                    to="#"
                    onClick={(e) => { e.preventDefault(); handleForgotPassword(); }}
                    style={{ color: "#a78bfa", fontSize: "0.8125rem", textDecoration: "none" }}
                  >
                    Forgot password?
                  </NavLink>
                </Box>

                <LoadingButton
                  disabled={!isOnline}
                  type="submit"
                  loading={loading}
                  fullWidth
                  variant="contained"
                  sx={{
                    py: 1.25,
                    borderRadius: 3,
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    background: "linear-gradient(90deg, #667eea, #764ba2)",
                    boxShadow: "0 4px 20px rgba(102,126,234,0.4)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #5a6fd6, #6a4190)",
                      boxShadow: "0 6px 24px rgba(102,126,234,0.55)",
                    },
                    "&.Mui-disabled": { opacity: 0.5 },
                  }}
                >
                  {otpSent ? "Verify & Sign In" : "Send OTP"}
                </LoadingButton>

                {otpSent && (
                  <Box textAlign="center" mt={2}>
                    <Typography
                      variant="body2"
                      color="rgba(255,255,255,0.45)"
                      sx={{ cursor: "pointer", "&:hover": { color: "#a78bfa" } }}
                      onClick={() => { setOtpSent(false); setError(""); setInfo(""); formik.resetForm(); }}
                    >
                      ← Back to login
                    </Typography>
                  </Box>
                )}
              </form>

              <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.08)" }} />
              <Typography variant="caption" color="rgba(255,255,255,0.3)" textAlign="center">
                Need help?{" "}
                <Box component="a" href="mailto:support@dahiyainsurance.com" sx={{ color: "#a78bfa" }}>
                  support@dahiyainsurance.com
                </Box>
              </Typography>
            </RightPanel>
          </Grid>
        </Grid>
      </GlassCard>
    </StyledRoot>
  );
}
